import { getBundleData } from "containers/Bundles/Bundles.selectors";
import { getProjectData } from "containers/Projects/Projects.selectors";
import { canLoadFileKey } from "containers/Resources/screens/ResourcesSingle/utils";
import { getWorkspaceRecords } from "containers/Workspaces/Workspaces.selectors";
import { keyToForceUpdate } from "utils/RouterManager/constants";

export const getSingleResourceFetching = (state) => state?.resources?.singleFetching || [];

export const getResourceData = (state, id) => {
    if (!id) return null;
    return state.resources?.[id];
};

const setRecordUserData = (recordUserData, recordListInProject, id, { isPinned }) => {
    const foundRecord = recordListInProject?.find((record) => record.record_id_record?.id === id);

    if (foundRecord != null) {
        recordUserData.isPinned = isPinned;
        recordUserData.id = foundRecord.id;
    }
};

export const getRecordUserData = (state, id, projectOrBundleId) => {
    const recordUserData = {};
    const project = getProjectData(state, projectOrBundleId);
    const parentData = projectOrBundleId
        ? project ?? getBundleData(state, projectOrBundleId)
        : getWorkspaceRecords(state);

    if (parentData) {
        setRecordUserData(recordUserData, parentData.pinned, id, { isPinned: true });
        if (!recordUserData.id) {
            setRecordUserData(recordUserData, parentData.opened, id, { isPinned: false });
        }
    }

    return recordUserData;
};

export const getLogListData = (state, recordType, id) => {
    if (!id || !recordType) return null;
    return state.resources?.[`/${recordType}/${id}/log-list`];
};

export const getSavepointListData = (state, executionId) => {
    if (!executionId) return null;
    return state.resources?.[`/execution/${executionId}/savepoint-list`];
};

export const resourceDataSelector = (iri) => (state) => getResourceData(state, iri);
export const resourceDataFetchingSelector = (recordId) => (state) =>
    getSingleResourceFetching(state, recordId);

export const existingResourceNameSelector = (state) => state?.dataStore?.isNameExist?.resource;

// Stable empty-array identity for the "slice not loaded yet" case. A fresh `?? []`
// literal returns a NEW array on every selector call, so while the slice is unloaded
// (e.g. cold page load before the schema fetch resolves) `useSelector` sees a changing
// reference every render — invalidating downstream useMemo chains and forcing a
// re-render on every store update. Returning one frozen constant keeps the identity
// stable until the real slice arrives.
const EMPTY_SCHEMA = Object.freeze([]);

// Same stable-identity guard as EMPTY_SCHEMA, for non-schema list slices. Used by
// the git selectors below, which feed RouterContext (allUncommittedData /
// gitLockData). A fresh `?? []` there returns a new array on every store dispatch
// while the slice is absent (the normal case — no git write in flight), so
// `useSelector` sees a changed reference on EVERY action, re-rendering RouterManager
// and handing every RouterContext consumer (all live dashboard widgets) a new
// context value → a full dashboard re-render storm on an active workspace.
const EMPTY_LIST = Object.freeze([]);

export const connectorInputSchemasSelector = (state) =>
    state?.dataStore?.connectorInputSchemas ?? EMPTY_SCHEMA;

export const schemaSelector = (state) => state?.dataStore?.schema ?? EMPTY_SCHEMA;

export const objectTemplateSchemaSelector = (state) =>
    state?.dataStore?.objectTemplateSchema ?? EMPTY_SCHEMA;

export const gitLogPlainListSelector = (state) => state?.dataStore?.gitLogPlainList ?? EMPTY_LIST;

export const gitWriteLockSelector = (state) => state?.dataStore?.gitWriteLock ?? EMPTY_LIST;

export const canLoadFileContentSelector = (iri) => (state) =>
    state?.dataStore?.[canLoadFileKey(iri)];

export const getKeyToForceUpdate = (state) => state?.dataStore?.[keyToForceUpdate];

export const getTemporaryRecordData = (state) => state?.resources?.temporaryRecordData ?? {};

export const getTrashStateSelector = (state) => state?.dataStore?.trashState ?? [];

export const getListActionProgress = (state) => state?.resources?.listActionProgress ?? null;
